import {
  type ResourceBindingDefinition,
  resourceBinding,
} from '@seen/core/resourceBindingRegistry'
import { IconVariable } from '@tabler/icons-react'
import { type NodeViewProps, NodeViewWrapper } from '@tiptap/react'
import type { JSX } from 'react'
import { Popover } from '#src/components/core/popover/Popover.tsx'
import { PopoverContent } from '#src/components/core/popover/PopoverContent.tsx'
import { PopoverTrigger } from '#src/components/core/popover/PopoverTrigger.tsx'
import { Tooltip } from '#src/components/core/tooltip/Tooltip.tsx'
import { TooltipContent } from '#src/components/core/tooltip/TooltipContent.tsx'
import { TooltipTrigger } from '#src/components/core/tooltip/TooltipTrigger.tsx'
import {
  registryVariableContextLabel,
  registryVariableDeferredHint,
  registryVariableMissingContextReason,
  registryVariableSourceLabel,
} from '#src/components/rich-text-editor/registry-variable/registryVariableResolution.ts'

export interface RegistryVariableChipProps {
  id: string | null
  binding: ResourceBindingDefinition | undefined
  value: string | null
}

// Read-only on purpose: an edit here would never reach the source record, so
// a resolved chip only says where its value came from. The dotted underline
// and tint stay on at rest, so a resolved value reads as a variable without
// hovering it.
export function RegistryVariableChip({
  id,
  binding,
  value,
}: RegistryVariableChipProps): JSX.Element {
  const label = binding?.label ?? id ?? 'Unknown variable'

  if (value === null) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            data-testid="registry-variable-deferred"
            className="inline-flex h-5 items-center gap-0.5 rounded-[2px] border border-dashed border-stone-500 bg-frame px-1.5 align-top text-sm leading-[18px] text-stone-500"
          >
            <IconVariable className="size-3.5 shrink-0" />
            {label}
          </span>
        </TooltipTrigger>
        <TooltipContent>
          {binding
            ? registryVariableDeferredHint(binding.context)
            : 'This variable is no longer available'}
        </TooltipContent>
      </Tooltip>
    )
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <span
          data-testid="registry-variable-value"
          className="cursor-pointer rounded-xs bg-muted/40 px-0.5 underline decoration-muted-foreground/60 decoration-dotted underline-offset-2 hover:bg-muted hover:decoration-foreground"
        >
          {value}
        </span>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto max-w-72 p-2">
        <p className="flex items-center gap-1 text-xs text-muted-foreground">
          <IconVariable className="size-3.5 shrink-0" />
          {binding
            ? `${label} · ${registryVariableSourceLabel(binding.context)}`
            : label}
        </p>
      </PopoverContent>
    </Popover>
  )
}

export function RegistryVariableNodeView(props: NodeViewProps): JSX.Element {
  const id = props.node.attrs.id as string | null
  return (
    <NodeViewWrapper as="span" className="inline">
      <RegistryVariableChip
        id={id}
        binding={resourceBinding(id)}
        value={props.node.attrs.value as string | null}
      />
    </NodeViewWrapper>
  )
}

// A row in the @ picker's Variables level. An unavailable row says which
// context it is waiting for, since the row itself cannot be picked.
export function RegistryVariableOptionLabel({
  binding,
  available,
}: {
  binding: ResourceBindingDefinition
  available: boolean
}): JSX.Element {
  const contextLabel = registryVariableContextLabel(binding.context)
  return (
    <div className="flex items-center gap-2">
      <div className="p-0.5">
        <IconVariable className="size-4 shrink-0" />
      </div>
      <span>
        {binding.label}
        <span className="text-muted-foreground">
          {' '}
          ·{' '}
          {available
            ? contextLabel
            : `${contextLabel}, ${registryVariableMissingContextReason(binding.context)}`}
        </span>
      </span>
    </div>
  )
}
